import { type FC, memo } from 'react';

import type { ContractInsuranceEntityDetail } from '@/types/contract';
import { Button } from '@/ui';
import AutocompleteMultiSelect from '@/ui/components/AutocompleteMultiSelect';
import type { OptionType } from '@/ui/components/Form/Select/types';

import './styles.css';
import type { ContractEntityFilterValues, Props } from './types';

// Distinct options for one entity field, sorted by label.
const entityOptions = (
  entities: ContractInsuranceEntityDetail[],
  option: (entity: ContractInsuranceEntityDetail) => OptionType<string>
) =>
  [...new Map(entities.map((entity) => [option(entity).value, option(entity)])).values()].sort(
    (a, b) => a.label.localeCompare(b.label)
  );

const fields: {
  key: keyof ContractEntityFilterValues;
  label: string;
  option: (entity: ContractInsuranceEntityDetail) => OptionType<string>;
}[] = [
  {
    key: 'providers',
    label: 'Insurance provider',
    option: (entity) => ({
      value: entity.insurance_provider_id,
      label: entity.insurance_provider_name
    })
  },
  {
    key: 'types',
    label: 'Insurance type',
    option: (entity) => ({ value: entity.insurance_type, label: entity.insurance_type })
  },
  {
    key: 'jurisdictions',
    label: 'Jurisdiction',
    option: (entity) => ({ value: entity.jurisdiction_id, label: entity.jurisdiction_name })
  }
];

const ContractEntityFilters: FC<Props> = ({ entities, values, onChange }) => {
  const isFiltering = fields.some(({ key }) => values[key].length > 0);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {fields.map(({ key, label, option }) => (
        // The label stays as the field's accessible name; the placeholder shows it visually.
        <div key={key} className="min-w-48 flex-1">
          <AutocompleteMultiSelect
            label={label}
            showLabel={false}
            placeholder={label}
            options={entityOptions(entities, option)}
            selectedOptions={values[key]}
            onChange={(selected) => onChange({ ...values, [key]: selected })}
            debounce={false}
          />
        </div>
      ))}
      {isFiltering && (
        <Button
          variant="quiet"
          color="secondary"
          size="sm"
          onClick={() => onChange({ providers: [], types: [], jurisdictions: [] })}>
          Clear filters
        </Button>
      )}
    </div>
  );
};

export default memo(ContractEntityFilters);
